import { jwtDecode } from "jwt-decode";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DecodedAccessToken,
    ImpersonationResponse,
    MutationResult,
    QueryResult,
    SetPinRequest,
    TokenDto,
    UpdateProfilePasswordRequest,
    User,
    UserProfile,
} from "../models";
import { defaultAuthState, useAuth, useProfile, useToasts } from "../contexts";

type ImpersonateMutation = MutationResult<ImpersonationResponse, unknown, string, ImpersonationResponse>;

type UnimpersonateMutation = MutationResult<ImpersonationResponse, unknown, unknown, ImpersonationResponse>;

export interface IUseProfileQuery {
    getMyUserProfile: QueryResult<UserProfile>;
    updateMyUserProfile: MutationResult<UserProfile>;
    updateEmailMutation: MutationResult<TokenDto, unknown, string>;
    updatePasswordMutation: MutationResult<UpdateProfilePasswordRequest>;
    updatePinMutation: MutationResult<UserProfile, unknown, SetPinRequest>;
    logImpersonateMutation: ImpersonateMutation;
    impersonateMutation: ImpersonateMutation;
    unimpersonateMutation: UnimpersonateMutation;
}

/**
 * These mutations pertain only to the current user
 * @returns IUseProfileQuery
 */
export const useProfileQuery = (): IUseProfileQuery => {
    const { get, post, put, deleteWithContent } = useAxios();
    const queryClient = useQueryClient();
    const { showToast } = useToasts();
    const { setAuthState, isAuthorized, isRefreshing } = useAuth();
    const { setImpersonator, userIsLoading } = useProfile();

    const getMyUserProfile = useGenericQuery({
        queryFn: (config) => get<UserProfile>({ url: Endpoints.UserProfile, config }),
        queryKey: queryKeys.profile.myProfile(),
        enabled: isAuthorized && !isRefreshing && !userIsLoading,
    });

    const updateMyUserProfile = useGenericMutation({
        mutationFn: (userProfile: Partial<UserProfile>) =>
            put<Partial<UserProfile>, UserProfile>({ url: Endpoints.UserProfile, data: userProfile }),
        queryKey: queryKeys.profile.myProfile(),
        successToast: "Profile updated",
    });

    const updatePinMutation = useGenericMutation({
        mutationFn: (data: SetPinRequest) =>
            put<SetPinRequest, UserProfile>({ url: `${Endpoints.UserProfile}/pin`, data }),
        queryKey: queryKeys.profile.myProfile(),
        successToast: "PIN updated",
    });

    const updateEmailMutation = useGenericMutation({
        mutationFn: (email: string) =>
            post<string, TokenDto>({
                url: Endpoints.UpdateEmail,
                data: email,
                config: { headers: { "Content-Type": "application/json" } },
            }),
        queryKey: queryKeys.profile.me(),
        invalidateQueryKey: false,
        onSuccess: async (data) => {
            const { accessToken, refreshToken } = data;
            const decodedAccessToken = jwtDecode<DecodedAccessToken>(accessToken);
            setAuthState({
                ...defaultAuthState,
                accessToken,
                decodedAccessToken,
                isAuthenticated: true,
                refreshToken,
            });
            await Promise.all([queryKeys.profile.me()].map((key) => queryClient.invalidateQueries(key)));
            showToast({
                theme: "success",
                titleInHeader: "Success",
                body: "Your email has been updated.",
                name: "UPDATE_EMAIL_SUCCESS",
            });
        },
    });

    const updatePasswordMutation = useGenericMutation({
        mutationFn: (data: Partial<UpdateProfilePasswordRequest>) =>
            post<Partial<UpdateProfilePasswordRequest>, UpdateProfilePasswordRequest>({
                url: Endpoints.UpdateProfilePassword,
                data,
            }),
        queryKey: queryKeys.profile.updatePassword(),
        successToast: "Password updated",
    });

    const impersonateMutation = useGenericMutation({
        mutationFn: (email: string) =>
            post<string, ImpersonationResponse>({
                url: Endpoints.Impersonation,
                data: email,
                config: { headers: { "Content-Type": "application/json" } },
            }),
        queryKey: queryKeys.profile.impersonator(),
        invalidateQueryKey: false,
        onSuccess: async (data) => {
            if (!data.success) return;
            const { impersonatorId, userId, emailAddress } = data;

            const users = queryClient.getQueryData<User[]>(queryKeys.users.list());

            setImpersonator(users?.find((u) => u.id === impersonatorId));

            queryClient.setQueryData<User | undefined>(
                queryKeys.profile.me(),
                users?.find((u) => u.id === userId)
            );

            setAuthState((prev) => ({
                ...prev,
                decodedAccessToken: {
                    ...prev.decodedAccessToken,
                    user_id: userId.toString(),
                    email: emailAddress,
                },
            }));

            await Promise.all(
                [queryKeys.users.lists(), queryKeys.tenants.lists()].map((key) => queryClient.invalidateQueries(key))
            );

            showToast({
                theme: "success",
                titleInHeader: "Success",
                body: "Impersonation successful",
                name: "IMPERSONATION_SUCCESS",
            });
        },
    });

    const unimpersonateMutation = useGenericMutation({
        mutationFn: () => deleteWithContent<ImpersonationResponse>({ url: Endpoints.Impersonation }),
        queryKey: queryKeys.profile.impersonator(),
        invalidateQueryKey: false,
        onSuccess: async (data) => {
            if (!data.success) return;
            const { userId, emailAddress } = data;

            const users = queryClient.getQueryData<User[]>(queryKeys.users.list());

            setImpersonator(undefined);

            queryClient.setQueryData<User | undefined>(
                queryKeys.profile.me(),
                users?.find((u) => u.id === userId)
            );

            setAuthState((prev) => ({
                ...prev,
                decodedAccessToken: {
                    ...prev.decodedAccessToken,
                    user_id: userId.toString(),
                    email: emailAddress,
                },
            }));

            await Promise.all(
                [queryKeys.users.lists(), queryKeys.tenants.lists()].map((key) => queryClient.invalidateQueries(key))
            );

            showToast({
                theme: "success",
                titleInHeader: "Success",
                body: "Impersonation cancelled",
                name: "IMPERSONATION_SUCCESS",
            });
        },
    });

    const logImpersonateMutation = useGenericMutation({
        mutationFn: (data: string) =>
            post<string, ImpersonationResponse>({
                url: Endpoints.LogImpersonation,
                data,
                config: { headers: { "Content-Type": "application/json" } },
            }),
        queryKey: queryKeys.profile.impersonator(),
        invalidateQueryKey: false,
        disableSuccessToast: true,
        disableErrorToast: true,
    });

    return useMemo<IUseProfileQuery>(
        () => ({
            getMyUserProfile,
            updateMyUserProfile,
            updateEmailMutation,
            updatePinMutation,
            updatePasswordMutation,
            logImpersonateMutation,
            impersonateMutation,
            unimpersonateMutation,
        }),
        [
            getMyUserProfile,
            updateMyUserProfile,
            updateEmailMutation,
            updatePinMutation,
            updatePasswordMutation,
            logImpersonateMutation,
            impersonateMutation,
            unimpersonateMutation,
        ]
    );
};
